import {
  holdWindowRemainingMs,
  isHoldWindowClosed,
  isHoldWindowClosing,
  type AdminOrderPaymentDto,
} from '@carcheck/shared';

/**
 * AUT-219 / AUT-223 — pure predicates for the Платёж tab's capture surface.
 *
 * Extracted from the components so the "which button, when" rule is a unit
 * test rather than a render assertion, and so the tab body stays under the
 * component line cap.
 *
 * These are UX ONLY. The backend validates the same transitions and answers
 * `409 payment_not_capturable` / `409 payment_not_reconcilable` when a stale
 * screen posts anyway — a stale admin screen is the normal case, not the
 * exceptional one. Nothing here is a security boundary.
 */

/** Only a live hold can be settled by hand. */
export const CAPTURABLE_STATUS = 'authorized';

/**
 * Only a row whose settlement answer was lost has an open question. Anything
 * else already knows what happened to it.
 */
export const RECONCILABLE_STATUS = 'capturing';

/** `POST /admin/payments/:id/capture` applies. */
export const isCapturable = (payment: AdminOrderPaymentDto): boolean =>
  payment.status === CAPTURABLE_STATUS;

/** `POST /admin/payments/:id/reconcile-capture` applies. */
export const isReconcilable = (payment: AdminOrderPaymentDto): boolean =>
  payment.status === RECONCILABLE_STATUS;

/**
 * `capturing` is a claim state, not a resting one: the settle request went out
 * and the answer never came back. The UI must not paint it like a status the
 * row is allowed to sit in — a human owes it an answer.
 */
export const isTransientStatus = (status: string): boolean => status === RECONCILABLE_STATUS;

/**
 * AUT-301 — how the hold clock should be PAINTED, in three phases.
 *
 * The predicate itself is no longer ours: `isHoldWindowClosed` /
 * `isHoldWindowClosing` live in `@carcheck/shared/wire/payment-hold-window.ts`
 * because the server now reads the same calendar (`captureNow` used to gate on
 * `status !== 'authorized'` and nothing else — the local copy of this rule was
 * the ONLY thing standing between an operator and a settlement against a dead
 * authorization, and a hidden button is not a guard). One predicate, two
 * halves, no drift.
 *
 * What stays here is presentation: `closing` is a phase the shared module
 * expresses as "closing OR already closed" (deliberately, so callers asking
 * "should a human look at this" get one yes), and a UI needs the three states
 * separated to pick a colour. Order matters — `closed` is tested first.
 *
 * `remainingMs` is SIGNED: negative once the deadline passed, which is what
 * makes "истёк 6 часов назад" derivable instead of a bare "истёк".
 */
export type HoldWindowPhase = 'open' | 'closing' | 'closed';

export interface HoldWindowState {
  phase: HoldWindowPhase;
  /** Signed ms to `holdExpiresAt`; `null` when there is no deadline at all. */
  remainingMs: number | null;
}

export const describeHoldWindow = (
  holdExpiresAt: string | null,
  now: number = Date.now(),
): HoldWindowState => {
  const remainingMs = holdWindowRemainingMs(holdExpiresAt, now);
  if (isHoldWindowClosed(holdExpiresAt, now)) return { phase: 'closed', remainingMs };
  if (isHoldWindowClosing(holdExpiresAt, now)) return { phase: 'closing', remainingMs };
  return { phase: 'open', remainingMs };
};

/**
 * True once the hold clock is relevant at all — i.e. this is a two-stage
 * payment. Single-stage legacy rows carry neither timestamp and must not get a
 * "Холд поставлен: —" row that implies a hold they never had.
 */
export const hasHoldWindow = (payment: AdminOrderPaymentDto): boolean =>
  payment.authorizedAt !== null || payment.holdExpiresAt !== null;
